/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-negative-binomial.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import FormControl from '@mui/material/FormControl';
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'hooks';
import { PanelRateByType } from 'types/graphql-global-types';
import { checkRequiredFields, validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import CharactersRemaining from 'views/components/core/characters-remaining';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

import 'views/components/central-monitoring/panels/add-panel/styles.scss';

const Step2NegativeBinomial = forwardRef(
    ({ show, panelState, setPage, page, updateState, updateErrorState, canSave }: any, ref) => {
        const { t } = useTranslation();

        const initialState = {
            dataSource: panelState.dataSource.dataSource,
            countField: panelState.dataSource.countField,
            rateByField: panelState.dataSource.rateByField,
            rateByType: panelState.dataSource.rateByType,
            timePeriod: panelState.dataSource.timePeriod,
            unitMultiplication: panelState.modelParams.unitMultiplier,
            amberCutoff: panelState.modelParams.amberCutoff,
            redCutoff: panelState.modelParams.redCutoff,
            siteScoring: panelState.modelParams.siteScoring,
            greenMaxScore: panelState.modelParams.greenMaxScore,
            amberMaxScore: panelState.modelParams.amberMaxScore,
        };

        const initialErrors = {
            dataSource: '',
            countField: '',
            rateByField: '',
            rateByType: '',
            timePeriod: '',
            unitMultiplication: '',
            greenMaxScore: '',
            amberMaxScore: '',
            amberCutoff: '',
            redCutoff: '',
            siteScoring: '',
        } as any;

        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...initialErrors });

        const { unitMultiplications, siteScoringData, dataInclusionPeriods, rateByType } = PanelConstants;

        useEffect(() => {
            if (show)
                setState({
                    dataSource: panelState.dataSource.dataSource,
                    countField: panelState.dataSource.countField,
                    rateByField: panelState.dataSource.rateByField,
                    rateByType: panelState.dataSource.rateByType,
                    timePeriod: panelState.dataSource.timePeriod,
                    unitMultiplication: panelState.modelParams.unitMultiplier,
                    amberCutoff: panelState.modelParams.amberCutoff,
                    redCutoff: panelState.modelParams.redCutoff,
                    siteScoring: panelState.modelParams.siteScoring,
                    greenMaxScore: panelState.modelParams.greenMaxScore,
                    amberMaxScore: panelState.modelParams.amberMaxScore,
                });
        }, [panelState]);

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        };

        const numberValidation = (value: number, fieldName: string, min: number, max: number, errorText: string) => {
            let error = { [fieldName]: '' };
            if (value > max || value < min) error = { [fieldName]: errorText };
            setErrors({
                ...errors,
                ...error,
            });

            return !!error[fieldName];
        };

        const onChangeNumber =
            (fieldName: string, key?: string, min?: number, max?: number, errorText?: string) => (event: any) => {
                const { value } = event.target;
                let numberValidationError = null;
                let error = { [fieldName]: '' };
                setState({
                    ...state,
                    [fieldName]: +value,
                });
                updateState({ target: { name: key || fieldName, value: +value } });

                if (min !== undefined || max !== undefined)
                    numberValidationError = numberValidation(+value, fieldName, min, max, errorText);

                if (numberValidationError) return;

                if (fieldName === 'redCutoff' && +value >= state.amberCutoff)
                    error = { [fieldName]: t('admin:amberCutoffShouldBeBiggerThanRed') };
                else if (fieldName === 'amberCutoff' && +value <= state.redCutoff)
                    error = { [fieldName]: t('admin:amberCutoffShouldBeBiggerThanRed') };
                // If we're good, clear both
                else if (
                    (fieldName === 'redCutoff' && value < state.amberCutoff) ||
                    (fieldName === 'amberCutoff' && value > state.redCutoff)
                )
                    error = { ...error, redCutoff: null, amberCutoff: null };

                if (fieldName === 'amberMaxScore' && value <= state.greenMaxScore)
                    error = { [fieldName]: t('admin:amberMaxScoreShouldBeBiggerThenGreen') };
                if (fieldName === 'amberMaxScore' && value >= state.greenMaxScore)
                    error = {
                        ...error,
                        greenMaxScore: null,
                    };
                if (fieldName === 'greenMaxScore' && value >= state.amberMaxScore)
                    error = { [fieldName]: t('admin:amberMaxScoreShouldBeBiggerThenGreen') };
                if (fieldName === 'greenMaxScore' && value <= state.amberMaxScore)
                    error = {
                        ...error,
                        amberMaxScore: null,
                    };
                setErrors({
                    ...errors,
                    ...error,
                });
            };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value?: string) => {
            if (fieldName === 'dataSource') {
                updateState({ target: { name: 'dataSource.dataSource', value } });
                updateState({ target: { name: 'dataSource.rateByField', value: '' } });
                updateState({ target: { name: 'dataSource.countField', value: '' } });
            } else {
                updateState({ target: { name: key || fieldName, value } });
            }
            validate(value, fieldName, length, required);
        };

        const validationOnUpdate = () => {
            const requiredFields = [
                'dataSource',
                'countField',
                'rateByField',
                'rateByType',
                'greenMaxScore',
                'amberMaxScore',
                'amberCutoff',
                'redCutoff',
                'siteScoring',
            ];

            if (state.rateByType === PanelRateByType.TIME_PERIOD) requiredFields.push('timePeriod');
            else if (state.rateByType === PanelRateByType.COUNT) requiredFields.push('unitMultiplication');

            return checkRequiredFields({ fields: requiredFields, state, setErrors, errors });
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                updateState({ target: { name: 'modelParams.unitMultiplier', value: +state.unitMultiplication || 1 } });
                setPage({ ...page, number: 3, available: 4 });
            } else if (page.number !== 2) setPage({ ...page, number: 2, available: page.available });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        };

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="type-edc-wrapper mt mb-30">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <DataSourceSelector
                            label={t('admin:rowData')}
                            onChange={(value: string) => {
                                onChange('dataSource', null, false, 'dataSource.dataSource', value);
                            }}
                            fieldSelect="dataSource"
                            value={state.dataSource}
                            labelField="rowDataSource"
                            showDefault={false}
                            error={errors.dataSource}
                            customClassName="edit-atts-category"
                            searchable
                            tag="PANEL"
                            disabled={!canSave}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <FieldSelector
                            customClassName="add-panel-countField"
                            fieldSelect="countField"
                            onChange={(value: string) => {
                                onChange('countField', null, false, 'dataSource.countField', value);
                            }}
                            label={`${t('admin:count')}*`}
                            labelField="countField"
                            customId="add-panel-count"
                            value={state.countField}
                            error={errors.countField}
                            dataSource={state.dataSource}
                            formats={[Formats.Int]}
                            excludeValue={(field: any) => field.key !== state.rateByField}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mb-30">
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <FieldSelector
                            customClassName="add-panel-rateByField"
                            fieldSelect="rateByField"
                            onChange={(value: string) => {
                                onChange('rateByField', null, false, 'dataSource.rateByField', value);
                            }}
                            label={t('admin:rateBy')}
                            labelField="rateByField"
                            customId="add-panel-rate-by"
                            value={state.rateByField}
                            error={errors.rateByField}
                            dataSource={state.dataSource}
                            formats={[Formats.Int, Formats.Float]}
                            excludeValue={(field: any) => field.key !== state.countField}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mb-30">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <SelectWithCategories
                            customClassName="rate-by-type"
                            showWarning={false}
                            fieldSelect="rateByType"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'dataSource.rateByType', event.target?.value);
                            }}
                            label={t('admin:rateByType')}
                            filterable={false}
                            labelField="rateByType"
                            dataArray={rateByType}
                            customId="rate-by-type"
                            currentState={state.rateByType}
                            error={errors.rateByType}
                        />
                    </FormControl>
                    {state.rateByType === PanelRateByType.TIME_PERIOD ? (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <SelectWithCategories
                                customClassName="edit-control-review-frequency-units"
                                showWarning={false}
                                fieldSelect="timePeriod"
                                onChange={(fieldName?: string) => (event: any) => {
                                    onChange(fieldName, null, false, 'dataSource.timePeriod', event.target?.value);
                                }}
                                label={t('admin:timePeriod')}
                                filterable={false}
                                labelField="timePeriod"
                                dataArray={dataInclusionPeriods}
                                customId="time-period"
                                currentState={state.timePeriod}
                                error={errors.timePeriod}
                            />
                        </FormControl>
                    ) : null}
                    {state.rateByType === PanelRateByType.COUNT ? (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <SelectWithCategories
                                customClassName="edit-control-review-frequency-units"
                                showWarning={false}
                                fieldSelect="unitMultiplication"
                                onChange={(fieldName?: string) => (event: any) => {
                                    onChange(fieldName, null, false, 'modelParams.unitMultiplier', event.target?.value);
                                }}
                                label={t('admin:unitMultiplication')}
                                filterable={false}
                                labelField="unitMultiplication"
                                dataArray={unitMultiplications}
                                customId="unit-multiplication"
                                currentState={state.unitMultiplication}
                                error={errors.unitMultiplication}
                            />
                        </FormControl>
                    ) : null}
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={t('admin:amberCutOff')}
                            id="amber-cut-off"
                            value={state.amberCutoff}
                            InputProps={{ inputProps: { min: 0, max: 1, step: 0.05 } }}
                            onChange={onChangeNumber('amberCutoff', 'modelParams.amberCutoff')}
                            placeholder={t('enterValue')}
                            error={errors.amberCutoff}
                            variant="outlined"
                            type="number"
                            color="secondary"
                            className="before-select"
                            helperText={<CharactersRemaining id="amber-cut-off-text" error={errors.amberCutoff} />}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:redCutOff')}
                            id="red-cut-off"
                            value={state.redCutoff}
                            InputProps={{ inputProps: { min: 0, max: 1, step: 0.05 } }}
                            onChange={onChangeNumber('redCutoff', 'modelParams.redCutoff')}
                            placeholder={t('enterValue')}
                            error={errors.redCutoff}
                            type="number"
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            helperText={<CharactersRemaining id="red-cut-off-text" error={errors.redCutoff} />}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type">
                        <SelectWithCategories
                            customClassName="site-scoring"
                            showWarning={false}
                            fieldSelect="siteScoring"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'modelParams.siteScoring', event.target?.value);
                            }}
                            label={t('admin:siteScoring')}
                            filterable={false}
                            labelField="siteScoring"
                            dataArray={siteScoringData}
                            customId="site-scoring"
                            currentState={state.siteScoring}
                            error={errors.siteScoring}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={t('admin:greenMaxScoreR')}
                            id="green-max-score"
                            value={state.greenMaxScore}
                            onChange={onChangeNumber(
                                'greenMaxScore',
                                'modelParams.greenMaxScore',
                                1,
                                100,
                                t('admin:numericValueMinMax', { min: '1', max: '100' }),
                            )}
                            placeholder={t('admin:enterAmberMaxScoreR')}
                            error={errors.greenMaxScore}
                            type="number"
                            InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }}
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            helperText={
                                <span
                                    style={errors.greenMaxScore ? { color: '#ae0015' } : {}}
                                    className="remaining-text"
                                >
                                    {errors.greenMaxScore
                                        ? errors.greenMaxScore
                                        : t('admin:numericValueMinMax', { min: '1', max: '100' })}
                                </span>
                            }
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('admin:amberMaxScore')}
                            id="amber-max-score"
                            value={state.amberMaxScore}
                            InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }}
                            onChange={onChangeNumber(
                                'amberMaxScore',
                                'modelParams.amberMaxScore',
                                1,
                                100,
                                t('admin:numericValueMinMax', { min: '1', max: '100' }),
                            )}
                            error={errors.amberMaxScore}
                            variant="outlined"
                            color="secondary"
                            type="number"
                            className="before-select"
                            placeholder={t('admin:enterAmberMaxScoreR')}
                            helperText={
                                <span
                                    style={errors.amberMaxScore ? { color: '#ae0015' } : {}}
                                    className="remaining-text"
                                >
                                    {errors.amberMaxScore
                                        ? errors.amberMaxScore
                                        : t('admin:numericValueMinMax', { min: '1', max: '100' })}
                                </span>
                            }
                        />
                    </FormControl>
                </div>
            </div>
        );
    },
);

export default Step2NegativeBinomial;
